import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Space,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import {
  CloseOutlined,
  CommentOutlined,
  PictureOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import { useFileUpload } from "hooks/useFileUpload";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { PROFILE_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

const SUBJECT_CHIPS = [
  "GENERAL_FEEDBACK",
  "PERFORMANCE",
  "APP_BUG",
  "FEATURE_REQUEST",
  "UI_FEEDBACK",
  "PAYMENTS",
  "TASKS",
  "SAFETY",
  "HARASSMENT",
  "ACCOUNT",
  "VERIFICATION",
  "SUPPORT",
  "OTHER",
];

const FEELING_OPTIONS = [
  { key: "LIKE", label: "Like" },
  { key: "DISLIKE", label: "Dislike" },
  { key: "HAPPY", label: "Happy" },
  { key: "SAD", label: "Sad" },
];

const readable = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const Feedback = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const { uploadFile, uploading } = useFileUpload();
  const [subject, setSubject] = useState("GENERAL_FEEDBACK");
  const [feeling, setFeeling] = useState("LIKE");
  const [attachments, setAttachments] = useState([]);
  const [attachmentPreviews, setAttachmentPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const content = Form.useWatch("content", form) || "";
  const actorId = user?.id || user?.userId || "";
  const isSubmitting = submitting || uploading;
  const isDisabled = !String(content).trim() || !feeling || isSubmitting;

  useEffect(() => {
    const previews = attachments.map((file) => ({
      uid: file.uid,
      name: file.name,
      url: URL.createObjectURL(file),
    }));
    setAttachmentPreviews(previews);

    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [attachments]);

  const removeAttachment = (uid) => {
    setAttachments((current) => current.filter((file) => file.uid !== uid));
  };

  const handleSubmit = async (values) => {
    const trimmedContent = String(values.content || "").trim();
    if (!trimmedContent) {
      message.warning("Write your feedback before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      const attachmentIds = [];
      for (const file of attachments) {
        const result = await uploadFile(actorId, file, {
          purpose: "USER_PROFILE",
          fileType: "PHOTO",
          skipUserValidation: true,
        });
        if (result?.fileId) attachmentIds.push(result.fileId);
      }

      await apiClient.post(`${PROFILE_SERVICE_BASE_URL}/api/v1/feedback`, {
        subject,
        subjectId: "",
        feeling,
        content: trimmedContent,
        ...(attachmentIds.length ? { attachments: attachmentIds } : {}),
      });

      form.resetFields();
      setSubject("GENERAL_FEEDBACK");
      setFeeling("LIKE");
      setAttachments([]);
      message.success("Thank you. Your feedback has been sent.");
    } catch (error) {
      message.error(error?.message || "Failed to send feedback.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon={<CommentOutlined />}
          title="Feedback"
          subtitle="Tell us what you think and feel about the app"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
      </div>

      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        <Card
          style={{
            borderRadius: 8,
            borderColor: OLIVEEB_COLORS.oliveebGreenSoft,
            background: OLIVEEB_COLORS.card,
            boxShadow: "0 14px 34px rgba(6, 105, 2, 0.08)",
          }}
          styles={{ body: { padding: 18 } }}
        >
          <Text
            style={{
              display: "block",
              color: OLIVEEB_COLORS.oliveebGreen,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 1.2,
              textTransform: "uppercase",
            }}
          >
            We&apos;re listening
          </Text>
          <Paragraph style={{ color: OLIVEEB_COLORS.textMuted, margin: "6px 0 0" }}>
            Tell us what you think and feel about the app.
          </Paragraph>
        </Card>

        <Form form={form} layout="vertical" requiredMark={false} onFinish={handleSubmit}>
          <Card
            style={{ borderRadius: 8, borderColor: OLIVEEB_COLORS.border, marginBottom: 12 }}
            styles={{ body: { padding: 16 } }}
          >
            <Text strong style={{ display: "block", color: OLIVEEB_COLORS.oliveebGreen, marginBottom: 10 }}>
              Subject
            </Text>
            <Space size={[8, 8]} wrap>
              {SUBJECT_CHIPS.map((item) => {
                const active = subject === item;
                return (
                  <Tag.CheckableTag
                    key={item}
                    checked={active}
                    onChange={() => setSubject(item)}
                    style={{
                      marginInlineEnd: 0,
                      borderRadius: 8,
                      padding: "6px 10px",
                      border: `1px solid ${active ? OLIVEEB_COLORS.oliveebGreen : OLIVEEB_COLORS.border}`,
                      background: active ? OLIVEEB_COLORS.oliveebGreen : OLIVEEB_COLORS.bgSoft,
                      color: active ? OLIVEEB_COLORS.white : OLIVEEB_COLORS.textPrimary,
                      fontWeight: 700,
                    }}
                  >
                    {readable(item)}
                  </Tag.CheckableTag>
                );
              })}
            </Space>
          </Card>

          <Card
            style={{ borderRadius: 8, borderColor: OLIVEEB_COLORS.border, marginBottom: 12 }}
            styles={{ body: { padding: 16 } }}
          >
            <Text strong style={{ display: "block", color: OLIVEEB_COLORS.oliveebGreen, marginBottom: 10 }}>
              How do you feel?
            </Text>
            <Space size={[8, 8]} wrap>
              {FEELING_OPTIONS.map((item) => {
                const active = feeling === item.key;
                return (
                  <Button
                    key={item.key}
                    type={active ? "primary" : "default"}
                    onClick={() => setFeeling(item.key)}
                    style={{
                      borderRadius: 8,
                      borderColor: active ? OLIVEEB_COLORS.oliveebGreen : OLIVEEB_COLORS.border,
                      background: active ? OLIVEEB_COLORS.oliveebGreen : OLIVEEB_COLORS.bgSoft,
                      color: active ? OLIVEEB_COLORS.white : OLIVEEB_COLORS.textPrimary,
                      fontWeight: 700,
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Space>
          </Card>

          <Card
            style={{ borderRadius: 8, borderColor: OLIVEEB_COLORS.border, marginBottom: 12 }}
            styles={{ body: { padding: 16 } }}
          >
            <Text strong style={{ display: "block", color: OLIVEEB_COLORS.oliveebGreen, marginBottom: 10 }}>
              Attachments
            </Text>
            {attachmentPreviews.length ? (
              <Space size={[10, 10]} wrap style={{ marginBottom: 12 }}>
                {attachmentPreviews.map((item) => (
                  <div
                    key={item.uid}
                    style={{
                      position: "relative",
                      width: 88,
                      height: 88,
                      borderRadius: 8,
                      overflow: "hidden",
                      border: `1px solid ${OLIVEEB_COLORS.border}`,
                      background: OLIVEEB_COLORS.bgSoft,
                    }}
                  >
                    <img
                      src={item.url}
                      alt={item.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <Button
                      type="primary"
                      danger
                      size="small"
                      shape="circle"
                      icon={<CloseOutlined />}
                      aria-label={`Remove ${item.name}`}
                      onClick={() => removeAttachment(item.uid)}
                      style={{ position: "absolute", top: 4, right: 4 }}
                    />
                  </div>
                ))}
              </Space>
            ) : null}
            <Upload
              accept="image/*"
              multiple
              showUploadList={false}
              beforeUpload={(file) => {
                setAttachments((current) => [...current, file]);
                return false;
              }}
            >
              <Button icon={<PictureOutlined />}>Add photo</Button>
            </Upload>
          </Card>

          <Card
            style={{ borderRadius: 8, borderColor: OLIVEEB_COLORS.border, marginBottom: 12 }}
            styles={{ body: { padding: 16 } }}
          >
            <Form.Item
              label={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Your feedback</Text>}
              name="content"
              rules={[{ required: true, message: "Write your feedback before submitting." }]}
            >
              <TextArea
                rows={7}
                maxLength={1200}
                showCount
                placeholder="Write your feedback here..."
              />
            </Form.Item>
          </Card>

          <Button
            type="primary"
            htmlType="submit"
            icon={<SendOutlined />}
            loading={isSubmitting}
            disabled={isDisabled}
            style={{
              minWidth: 190,
              height: 44,
              borderRadius: 8,
              fontWeight: 900,
              background: isDisabled ? OLIVEEB_COLORS.placeholder : OLIVEEB_COLORS.oliveebGreen,
              borderColor: isDisabled ? OLIVEEB_COLORS.placeholder : OLIVEEB_COLORS.oliveebGreen,
            }}
          >
            Submit Feedback
          </Button>
        </Form>
      </Space>
    </div>
  );
};

export default Feedback;
